import React from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
  SxProps,
  Theme,
  Divider,
} from "@mui/material";

interface Year {
  id?: number;
  year?: number;
}

/** Sent when the user unchecks everything; matches no year (an empty list means "all years") */
export const NO_YEARS_SELECTED = -1;

const CHECK_ALL = "__checkAll__";
const UNCHECK_ALL = "__uncheckAll__";

interface YearFilterSelectProps {
  years: Year[];
  selectedYearIds: number[];
  onChange: (yearIds: number[]) => void;
  label?: string;
  fullWidth?: boolean;
  size?: "small" | "medium";
  sx?: SxProps<Theme>;
  /** Pipeline folder id; when set, "Pipeline" is offered on top (unchecked by default) */
  pipelineYearId?: number;
  /** Adds "Check all" / "Uncheck all" */
  bulkActions?: boolean;
}

export const YearFilterSelect: React.FC<YearFilterSelectProps> = ({
  years,
  selectedYearIds,
  onChange,
  label = "Years",
  fullWidth = true,
  size = "small",
  sx,
  pipelineYearId,
  bulkActions = false,
}) => {
  const allYearIds = years
    .map((y) => y.id)
    .filter((id): id is number => id !== undefined);

  // An empty selection means "all years" (never the Pipeline): show those as checked
  const checkedIds =
    bulkActions && selectedYearIds.length === 0
      ? allYearIds
      : selectedYearIds.filter((id) => id !== NO_YEARS_SELECTED);

  const handleChange = (e: SelectChangeEvent<(number | string)[]>) => {
    const value = e.target.value as (number | string)[];
    if (!bulkActions) {
      onChange(value as number[]);
      return;
    }
    if (value.includes(CHECK_ALL)) {
      onChange(
        pipelineYearId !== undefined
          ? [pipelineYearId, ...allYearIds]
          : [],
      );
      return;
    }
    if (value.includes(UNCHECK_ALL)) {
      onChange([NO_YEARS_SELECTED]);
      return;
    }
    const ids = value.filter((v): v is number => typeof v === "number");
    if (ids.length === 0) {
      onChange([NO_YEARS_SELECTED]);
    } else if (
      ids.length === allYearIds.length &&
      allYearIds.every((id) => ids.includes(id))
    ) {
      // Every year and no Pipeline: back to the canonical "all years"
      onChange([]);
    } else {
      onChange(ids);
    }
  };

  const yearName = (id: number) =>
    id === pipelineYearId
      ? "Pipeline"
      : years.find((y) => y.id === id)?.year?.toString();

  return (
    <FormControl fullWidth={fullWidth} size={size} sx={sx}>
      <InputLabel shrink={true}>{label}</InputLabel>
      <Select<(number | string)[]>
        multiple
        notched={true}
        value={checkedIds}
        displayEmpty
        onChange={handleChange}
        label={label}
        renderValue={() => {
          if (selectedYearIds.length === 0) {
            return "All Years";
          }
          if (checkedIds.length === 0) {
            return "None selected";
          }

          if (checkedIds.length > 3) {
            return `${checkedIds.length} selected`;
          }

          return checkedIds.map(yearName).join(", ");
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
            },
          },
        }}
      >
        {bulkActions && (
          <MenuItem value={CHECK_ALL} dense>
            <ListItemText primary="Check all" />
          </MenuItem>
        )}
        {bulkActions && (
          <MenuItem value={UNCHECK_ALL} dense>
            <ListItemText primary="Uncheck all" />
          </MenuItem>
        )}
        {bulkActions && <Divider />}
        {pipelineYearId !== undefined && (
          <MenuItem value={pipelineYearId}>
            <Checkbox checked={checkedIds.includes(pipelineYearId)} />
            <ListItemText primary="Pipeline" />
          </MenuItem>
        )}
        {pipelineYearId !== undefined && <Divider />}
        {years.map((year) => (
          <MenuItem key={year.id} value={year.id}>
            <Checkbox
              checked={year.id !== undefined && checkedIds.includes(year.id)}
            />
            <ListItemText primary={year.year} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
